import React from 'react'
import styled from 'styled-components'

import CollapsiblePanel from 'components/CollapsiblePanel'

import Header from 'pages/shared/Alert/Header'
import Details from 'pages/shared/Alert/Details'
import DeviceConnectionStatusTooltipContent from 'pages/shared/AlertsTooltip/DeviceConnectionStatusTooltipContent'

import { highestSeverity } from 'helpers/alerts'

import { color } from 'styles/colors'
import { Alert, DeviceConnectionState } from 'types/Alert'
import { screenSize, screenWidthBreakpoints } from 'styles/shared'
import BrowserCompatibility from 'helpers/browserCompatibility'

const BUBBLE_RADIUS = 16

type Props = {
  alerts: Array<Alert>,
  deviceConnectionState: Array<DeviceConnectionState> | null,
  displayAlertIds: string | null
}

const Alerts = ({ alerts, displayAlertIds, deviceConnectionState }: Props) => {
  const ids = displayAlertIds?.split('-') ?? []
  const matchedAlerts = alerts.filter(alert => ids.includes(alert.id)) ?? []
  const matchedDeviceConnectionState = deviceConnectionState?.filter(state => ids.includes(state.id)) ?? []
  const onlyDeviceConnectionStatusAlerts = matchedAlerts.length === 0 && matchedDeviceConnectionState.length > 1
  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)

  return (
    <>
      {matchedAlerts.length + matchedDeviceConnectionState.length > 0 && (
        <S.Container>
          {matchedAlerts.length > 0 && <S.Severity $severity={highestSeverity(matchedAlerts)} />}
          <S.ScrollContainer
            $isMobile={isMobile}
          >
            {matchedAlerts.length + matchedDeviceConnectionState.length > 1
              ? (
                <S.Alerts>
                  {matchedAlerts.map(alert => {
                    const { problems, possibleCauses, additionalInfo } = alert
                    return (
                      <S.AlertItem
                        key={`${alert.code} ${alert.occurredAt} ${alert.id}`}
                        caretVerticalPosition="flex-start"
                        caretSize="32px"
                        headerComponent={<Header alert={alert} />}
                      >
                        <S.Details {...{ problems, possibleCauses, additionalInfo }} />
                      </S.AlertItem>
                    )
                  })}
                  {matchedDeviceConnectionState.map(connection => (
                    <S.DeviceStatusContainer key={connection.id} $onlyDeviceConnectionStatusAlerts={onlyDeviceConnectionStatusAlerts}>
                      <DeviceConnectionStatusTooltipContent
                        state={connection.state}
                        occurredAt={connection.occurredAt}
                      />
                    </S.DeviceStatusContainer>
                  ))}
                </S.Alerts>
              )
              : matchedAlerts.length === 1 ? (
                <S.SingleAlert>
                  <Header alert={matchedAlerts[0]} />
                  <S.Details
                    problems={matchedAlerts[0].problems}
                    possibleCauses={matchedAlerts[0].possibleCauses}
                    additionalInfo={matchedAlerts[0].additionalInfo}
                  />
                </S.SingleAlert>
              ) : (
                <S.SingleDeviceStatusAlert>
                  <DeviceConnectionStatusTooltipContent
                    key={matchedDeviceConnectionState[0].id}
                    state={matchedDeviceConnectionState[0].state}
                    occurredAt={matchedDeviceConnectionState[0].occurredAt}
                  />
                </S.SingleDeviceStatusAlert>
              )
            }
          </S.ScrollContainer>
        </S.Container>
      )}
    </>
  )
}

export default Alerts

const S = {
  Container: styled.div`
    padding: 0;

    @media (min-width: ${screenWidthBreakpoints.minMedium}px) {
      position: relative;
      z-index: 1;
      overflow: hidden;
      border-radius: ${BUBBLE_RADIUS}px;
      background: ${color('paneBackground')};
    }
  `,
  ScrollContainer: styled.div<{ $isMobile: boolean }>`
    max-height: ${({ $isMobile }) => $isMobile ? 'calc(100dvh - 40px)' : '500px'};
    width: 100%;
    overflow-y: auto;
    border-radius: ${BUBBLE_RADIUS}px;

    @media ${screenSize.large} {
      width: ${({ $isMobile }) => ($isMobile ? '100%' : 'calc(59vw)')};
    }

    @media ${screenSize.mediumOnly} {
      width: ${({ $isMobile }) => ($isMobile ? '100%' : 'clamp(59vw, calc(46vw + 136.3px), 70vw)')};
    }

    @media ${screenSize.small} {
      width: ${({ $isMobile }) => ($isMobile ? '100%' : 'calc(88vw)')};
    }
  `,
  Alerts: styled.div`
    & > :first-child {
      padding-top: 10px;
    }

    & > :nth-child(2n) {
      background-color: ${color('alternatingRowBackground')};
    }

    & > :nth-child(2n + 1) {
      background-color: ${color('paneBackground')};
    }
  `,
  AlertItem: styled(CollapsiblePanel)`
    padding-right: 20px;
    padding-left: 10px;

    [class*='icon-arrow'] {
      margin-top: 10px;
    }
  `,
  DeviceStatusContainer: styled.div<{ $onlyDeviceConnectionStatusAlerts: boolean }>`
    padding-left: ${({ $onlyDeviceConnectionStatusAlerts }) =>
    $onlyDeviceConnectionStatusAlerts ? '20px' : '50px'};
  `,
  SingleAlert: styled.div`
    padding: 20px;
    background-color: ${color('paneBackground')};
  `,
  SingleDeviceStatusAlert: styled.div`
    width: 100%;
    padding: 20px;
    background-color: ${color('paneBackground')};
  `,
  Severity: styled.div<{ $severity: string | null }>`
    height: 10px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-top-left-radius: ${BUBBLE_RADIUS}px;
    border-top-right-radius: ${BUBBLE_RADIUS}px;
    background: ${({ $severity }) => color($severity + 'Alert')};
  `,
  Details: styled(Details)`
    padding: 0 20px 20px 20px;
    text-align: left;
  `
}
